import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import {
  getTemplate,
  createTemplate,
  updateTemplate,
  validateTemplate,
  CreateTemplateData,
} from '../../services/templateService';
import { getAssets } from '../../services/assetService';
import { getMeasurementDefinitions } from '../../services/measurementDefinitionService';
import { FormSection, FormField, MeasurementDefinition, TemplateMeasurementConfig } from '@easydone/shared';
import {
  PlusIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'number', label: 'Number' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'photo', label: 'Photo Upload' },
  { value: 'signature', label: 'Signature' },
];

export function TemplateBuilderPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const isEditMode = templateId !== 'new';

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [assetTypes, setAssetTypes] = useState<string[]>([]);
  const [availableAssetTypes, setAvailableAssetTypes] = useState<string[]>([]);
  const [applyToAllAssetTypes, setApplyToAllAssetTypes] = useState(false);
  const [sections, setSections] = useState<FormSection[]>([]);
  const [availableMeasurementDefs, setAvailableMeasurementDefs] = useState<MeasurementDefinition[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  // Load available asset types from assets in the system
  useEffect(() => {
    const loadAssetTypes = async () => {
      if (!currentOrg) return;
      try {
        const assets = await getAssets(currentOrg.id);
        const uniqueTypes = [...new Set(assets.map(a => a.type).filter(Boolean))].sort();
        setAvailableAssetTypes(uniqueTypes);
      } catch (error) {
        console.error('Failed to load asset types:', error);
      }
    };
    loadAssetTypes();
  }, [currentOrg]);

  // Load available measurement definitions
  useEffect(() => {
    const loadMeasurementDefs = async () => {
      if (!currentOrg) return;
      try {
        const defs = await getMeasurementDefinitions(currentOrg.id);
        setAvailableMeasurementDefs(defs);
      } catch (error) {
        console.error('Failed to load measurement definitions:', error);
      }
    };
    loadMeasurementDefs();
  }, [currentOrg]);

  useEffect(() => {
    if (isEditMode && templateId) {
      loadTemplate();
    } else {
      // Initialize new templates with a default Condition Score section
      setSections([
        {
          id: 'section-1',
          title: 'Condition Score',
          description: 'Rate the overall condition of the asset or location',
          order: 0,
          fields: [
            {
              id: 'field-1',
              type: 'dropdown',
              label: 'Condition Rating',
              required: true,
              order: 0,
              options: [
                '1 - Very Good (Like New)',
                '2 - Good (Minor Wear)',
                '3 - Moderate (Some Issues)',
                '4 - Poor (Significant Damage)',
                '5 - Very Poor (Failed/Critical)',
              ],
            },
          ],
        },
      ]);
    }
  }, [templateId, isEditMode]);

  const loadTemplate = async () => {
    if (!currentOrg || !templateId) return;

    try {
      const template = await getTemplate(currentOrg.id, templateId);
      if (template) {
        setName(template.name);
        setDescription(template.description || '');
        setCategory(template.category);
        setAssetTypes(template.assetTypes || []);
        setSections(template.sections);
      }
    } catch (error) {
      console.error('Failed to load template:', error);
      alert('Failed to load template');
      navigate('/templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentOrg) return;

    // Clean up sections - remove empty options from fields
    const cleanedSections = sections.map(section => ({
      ...section,
      fields: section.fields.map(field => ({
        ...field,
        options: field.options?.filter(opt => opt.trim() !== ''),
      })),
    }));

    const templateData: CreateTemplateData = {
      name,
      description,
      category,
      assetTypes,
      sections: cleanedSections,
      settings: {
        requirePhotos: false,
        allowOfflineEditing: true,
        requireReview: false,
      },
    };

    const validation = validateTemplate(templateData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setSaving(true);
    setErrors([]);

    try {
      if (isEditMode && templateId) {
        await updateTemplate(currentOrg.id, templateId, templateData);
      } else {
        await createTemplate(currentOrg.id, templateData);
      }
      navigate('/templates');
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const addSection = () => {
    const newSection: FormSection = {
      id: `section_${Date.now()}`,
      title: 'New Section',
      description: '',
      order: sections.length,
      fields: [],
    };
    setSections([...sections, newSection]);
  };

  const updateSection = (sectionId: string, updates: Partial<FormSection>) => {
    setSections(
      sections.map(section =>
        section.id === sectionId ? { ...section, ...updates } : section
      )
    );
  };

  const deleteSection = (sectionId: string) => {
    setSections(sections.filter(s => s.id !== sectionId));
  };

  const moveSection = (sectionId: string, direction: 'up' | 'down') => {
    const index = sections.findIndex(s => s.id === sectionId);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === sections.length - 1)
    ) {
      return;
    }

    const newSections = [...sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setSections(newSections);
  };

  const addField = (sectionId: string) => {
    const newField: FormField = {
      id: `field_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false,
      order: 0,
    };

    setSections(
      sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            fields: [...section.fields, { ...newField, order: section.fields.length }],
          };
        }
        return section;
      })
    );
  };

  const updateField = (sectionId: string, fieldId: string, updates: Partial<FormField>) => {
    setSections(
      sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            fields: section.fields.map(field =>
              field.id === fieldId ? { ...field, ...updates } : field
            ),
          };
        }
        return section;
      })
    );
  };

  const deleteField = (sectionId: string, fieldId: string) => {
    setSections(
      sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            fields: section.fields.filter(f => f.id !== fieldId),
          };
        }
        return section;
      })
    );
  };

  const addMeasurement = (sectionId: string, defId: string) => {
    const definition = availableMeasurementDefs.find(d => d.id === defId);
    if (!definition) return;

    setSections(
      sections.map(section => {
        if (section.id === sectionId) {
          // Check if already added to this section
          if (section.measurements?.some(m => m.definitionId === defId)) {
            alert('This measurement is already added to this section');
            return section;
          }

          const newMeasurement: TemplateMeasurementConfig = {
            ...definition,
            definitionId: defId,
            warningThreshold: undefined,
            criticalThreshold: undefined,
          };

          return {
            ...section,
            measurements: [...(section.measurements || []), newMeasurement],
          };
        }
        return section;
      })
    );
  };

  const updateMeasurement = (
    sectionId: string,
    index: number,
    updates: Partial<TemplateMeasurementConfig>
  ) => {
    setSections(
      sections.map(section => {
        if (section.id === sectionId && section.measurements) {
          return {
            ...section,
            measurements: section.measurements.map((m, i) =>
              i === index ? { ...m, ...updates } : m
            ),
          };
        }
        return section;
      })
    );
  };

  const deleteMeasurement = (sectionId: string, index: number) => {
    setSections(
      sections.map(section => {
        if (section.id === sectionId && section.measurements) {
          return {
            ...section,
            measurements: section.measurements.filter((_, i) => i !== index),
          };
        }
        return section;
      })
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading template...</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Edit Template' : 'Create Template'}
        </h1>
        <p className="text-gray-600 mt-1">Build your inspection form template</p>
      </div>

      {/* Validation Errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-red-900 mb-2">Please fix the following errors:</h3>
          <ul className="list-disc list-inside text-sm text-red-800 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Template Details */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Template Details</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input w-full"
                placeholder="e.g., Water Meter Inspection"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="input w-full"
                placeholder="e.g., Water, Electrical, HVAC"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input w-full"
              rows={3}
              placeholder="Describe this template..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asset Types
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Select which asset types this template applies to
            </p>

            {/* All Asset Types Option */}
            <label className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors mb-3">
              <input
                type="checkbox"
                checked={applyToAllAssetTypes}
                onChange={(e) => {
                  setApplyToAllAssetTypes(e.target.checked);
                  if (e.target.checked) {
                    setAssetTypes([]);
                  }
                }}
                className="mr-3"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">All Asset Types</div>
                <p className="text-xs text-gray-600 mt-0.5">This template will apply to all asset types</p>
              </div>
            </label>

            {/* Available Asset Types */}
            {!applyToAllAssetTypes && availableAssetTypes.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4 space-y-2 max-h-60 overflow-y-auto">
                {availableAssetTypes.map((type) => (
                  <label key={type} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={assetTypes.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAssetTypes([...assetTypes, type]);
                        } else {
                          setAssetTypes(assetTypes.filter(t => t !== type));
                        }
                      }}
                      className="mr-3"
                    />
                    <span className="text-sm text-gray-900">{type}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Fallback if no asset types in system */}
            {!applyToAllAssetTypes && availableAssetTypes.length === 0 && (
              <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
                No asset types found in your system. Create assets first, or check "All Asset Types" above.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4 mb-6">
        {sections.map((section, sectionIndex) => (
          <SectionEditor
            key={section.id}
            section={section}
            sectionIndex={sectionIndex}
            totalSections={sections.length}
            availableMeasurementDefs={availableMeasurementDefs}
            onUpdate={(updates) => updateSection(section.id, updates)}
            onDelete={() => deleteSection(section.id)}
            onMove={(direction) => moveSection(section.id, direction)}
            onAddField={() => addField(section.id)}
            onUpdateField={(fieldId, updates) => updateField(section.id, fieldId, updates)}
            onDeleteField={(fieldId) => deleteField(section.id, fieldId)}
            onAddMeasurement={(defId) => addMeasurement(section.id, defId)}
            onUpdateMeasurement={(index, updates) => updateMeasurement(section.id, index, updates)}
            onDeleteMeasurement={(index) => deleteMeasurement(section.id, index)}
          />
        ))}
      </div>

      {/* Add Section Button */}
      <button
        onClick={addSection}
        className="btn btn-secondary w-full mb-6 flex items-center justify-center"
      >
        <PlusIcon className="w-5 h-5 mr-2" />
        Add Section
      </button>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <button
          onClick={() => navigate('/templates')}
          className="btn btn-secondary"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="btn btn-primary"
          disabled={saving}
        >
          {saving ? 'Saving...' : isEditMode ? 'Update Template' : 'Create Template'}
        </button>
      </div>
    </div>
  );
}

interface SectionEditorProps {
  section: FormSection;
  sectionIndex: number;
  totalSections: number;
  availableMeasurementDefs: MeasurementDefinition[];
  onUpdate: (updates: Partial<FormSection>) => void;
  onDelete: () => void;
  onMove: (direction: 'up' | 'down') => void;
  onAddField: () => void;
  onUpdateField: (fieldId: string, updates: Partial<FormField>) => void;
  onDeleteField: (fieldId: string) => void;
  onAddMeasurement: (defId: string) => void;
  onUpdateMeasurement: (index: number, updates: Partial<TemplateMeasurementConfig>) => void;
  onDeleteMeasurement: (index: number) => void;
}

function SectionEditor({
  section,
  sectionIndex,
  totalSections,
  availableMeasurementDefs,
  onUpdate,
  onDelete,
  onMove,
  onAddField,
  onUpdateField,
  onDeleteField,
  onAddMeasurement,
  onUpdateMeasurement,
  onDeleteMeasurement,
}: SectionEditorProps) {
  return (
    <div className="card bg-gray-50">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <input
            type="text"
            value={section.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
            className="input font-semibold text-lg mb-2"
            placeholder="Section Title"
          />
          <input
            type="text"
            value={section.description || ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
            className="input text-sm"
            placeholder="Section description (optional)"
          />
        </div>
        <div className="flex space-x-2 ml-4">
          <button
            onClick={() => onMove('up')}
            disabled={sectionIndex === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <ArrowUpIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => onMove('down')}
            disabled={sectionIndex === totalSections - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <ArrowDownIcon className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1 text-gray-400 hover:text-red-600"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Fields */}
      {section.fields.length > 0 && (
        <div className="space-y-3 mb-3">
          {section.fields.map((field) => (
            <FieldEditor
              key={field.id}
              field={field}
              onUpdate={(updates) => onUpdateField(field.id, updates)}
              onDelete={() => onDeleteField(field.id)}
            />
          ))}
        </div>
      )}

      {/* Measurements */}
      {section.measurements && section.measurements.length > 0 && (
        <div className="space-y-3 mb-3">
          {section.measurements.map((measurement, index) => (
            <MeasurementEditor
              key={index}
              measurement={measurement}
              onUpdate={(updates) => onUpdateMeasurement(index, updates)}
              onDelete={() => onDeleteMeasurement(index)}
            />
          ))}
        </div>
      )}

      {/* Add Buttons */}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <button
          onClick={onAddField}
          className="btn btn-secondary btn-sm flex items-center justify-center"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Field
        </button>
        {availableMeasurementDefs.length > 0 ? (
          <select
            onChange={(e) => {
              if (e.target.value) {
                onAddMeasurement(e.target.value);
                e.target.value = '';
              }
            }}
            className="input input-sm"
          >
            <option value="">+ Add Measurement</option>
            {availableMeasurementDefs
              .filter(def => !section.measurements?.some(m => m.definitionId === def.id))
              .map(def => (
                <option key={def.id} value={def.id}>
                  {def.name} ({def.unit || def.dataType})
                </option>
              ))}
          </select>
        ) : (
          <a
            href="/measurement-definitions"
            className="btn btn-secondary btn-sm flex items-center justify-center text-xs"
          >
            <BeakerIcon className="w-4 h-4 mr-1" />
            Create Definitions
          </a>
        )}
      </div>
    </div>
  );
}

interface FieldEditorProps {
  field: FormField;
  onUpdate: (updates: Partial<FormField>) => void;
  onDelete: () => void;
}

function FieldEditor({ field, onUpdate, onDelete }: FieldEditorProps) {
  const needsOptions = field.type === 'dropdown' || field.type === 'radio' || field.type === 'checkbox';

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200">
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={field.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              className="input"
              placeholder="Field Label"
            />
            <select
              value={field.type}
              onChange={(e) => onUpdate({ type: e.target.value as any })}
              className="input"
            >
              {FIELD_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {needsOptions && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Options (one per line)
              </label>
              <textarea
                value={(field.options || []).join('\n')}
                onChange={(e) => onUpdate({ options: e.target.value.split('\n') })}
                className="input text-sm w-full"
                rows={3}
                placeholder="Option 1&#10;Option 2&#10;Option 3"
              />
            </div>
          )}

          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) => onUpdate({ required: e.target.checked })}
                className="mr-2"
              />
              Required
            </label>
            {field.type === 'text' || field.type === 'textarea' ? (
              <input
                type="text"
                value={field.placeholder || ''}
                onChange={(e) => onUpdate({ placeholder: e.target.value })}
                className="input input-sm flex-1"
                placeholder="Placeholder text..."
              />
            ) : null}
          </div>
        </div>

        <button
          onClick={onDelete}
          className="p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

interface MeasurementEditorProps {
  measurement: TemplateMeasurementConfig;
  onUpdate: (updates: Partial<TemplateMeasurementConfig>) => void;
  onDelete: () => void;
}

function MeasurementEditor({ measurement, onUpdate, onDelete }: MeasurementEditorProps) {
  return (
    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
      <div className="flex items-start gap-3">
        <BeakerIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
        <div className="flex-1 space-y-3">
          <div>
            <h4 className="font-medium text-gray-900">{measurement.name}</h4>
            <p className="text-sm text-gray-600">{measurement.description}</p>
            <div className="text-xs text-gray-500 mt-1">
              Type: {measurement.dataType} {measurement.unit && `• Unit: ${measurement.unit}`}
            </div>
          </div>

          {/* Threshold Configuration for numeric measurements */}
          {measurement.dataType === 'number' && (
            <>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-blue-300">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Warning Threshold
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={measurement.warningThreshold?.min ?? ''}
                      onChange={(e) => onUpdate({
                        warningThreshold: {
                          ...measurement.warningThreshold,
                          min: e.target.value ? Number(e.target.value) : undefined,
                        },
                      })}
                      className="input input-sm w-full"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={measurement.warningThreshold?.max ?? ''}
                      onChange={(e) => onUpdate({
                        warningThreshold: {
                          ...measurement.warningThreshold,
                          max: e.target.value ? Number(e.target.value) : undefined,
                        },
                      })}
                      className="input input-sm w-full"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Critical Threshold
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={measurement.criticalThreshold?.min ?? ''}
                      onChange={(e) => onUpdate({
                        criticalThreshold: {
                          ...measurement.criticalThreshold,
                          min: e.target.value ? Number(e.target.value) : undefined,
                        },
                      })}
                      className="input input-sm w-full"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={measurement.criticalThreshold?.max ?? ''}
                      onChange={(e) => onUpdate({
                        criticalThreshold: {
                          ...measurement.criticalThreshold,
                          max: e.target.value ? Number(e.target.value) : undefined,
                        },
                      })}
                      className="input input-sm w-full"
                    />
                  </div>
                </div>
              </div>
              <div className="pt-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Max Change % (optional)
                </label>
                <input
                  type="number"
                  placeholder="e.g., 20 (warns if change > 20%)"
                  value={measurement.maxChangePercent ?? ''}
                  onChange={(e) => onUpdate({
                    maxChangePercent: e.target.value ? Number(e.target.value) : undefined,
                  })}
                  className="input input-sm w-full"
                  min="0"
                  max="100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Requires confirmation if reading changes by more than this percentage from last value
                </p>
              </div>
            </>
          )}
        </div>

        <button
          onClick={onDelete}
          className="p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
